import type { PostFormData } from "@circle-react/api/postApi";
import { postApi } from "@circle-react/api/postApi";
import type { PostFormValues } from "@circle-react/components/QuickPostV3/data/types";
import type { EditablePost } from "@circle-react/components/QuickPostV3/data/types";
import type { Post } from "@circle-react/types";
import { omit } from "lodash";

export interface MetaTagAttributes {
  canonical_url?: string | null;
  meta_description?: string;
  meta_title?: string;
  opengraph_description?: string;
  opengraph_image?: string | null;
  opengraph_image_id?: number | null;
  opengraph_title?: string;
}

export interface QuickPostV3CreateOrUpdateParams {
  formData: Partial<PostFormValues>;
  post: Partial<Pick<Post, "id" | "slug" | "space_id">>;
}

export interface PostSettingsFormValues {
  cardview_thumbnail?: string | null;
  created_at?: string | null;
  hide_from_featured_areas?: boolean;
  hide_meta_info?: boolean;
  internal_custom_html?: string | null;
  is_comments_closed?: boolean;
  is_comments_disabled?: boolean;
  is_liking_disabled?: boolean;
  is_truncation_disabled?: boolean;
  meta_tag_attributes?: MetaTagAttributes;
  pin_to_top?: boolean;
  pinned_at?: string | null;
  slug?: string;
  user_id?: number | null;
}

export const CREATE_POST_SETTINGS_DEFAULTS: Required<
  Pick<
    PostSettingsFormValues,
    | "hide_from_featured_areas"
    | "hide_meta_info"
    | "is_comments_closed"
    | "is_comments_disabled"
    | "is_liking_disabled"
    | "is_truncation_disabled"
    | "pin_to_top"
  >
> & { meta_tag_attributes: MetaTagAttributes } = {
  hide_from_featured_areas: false,
  hide_meta_info: false,
  is_comments_closed: false,
  is_comments_disabled: false,
  is_liking_disabled: false,
  is_truncation_disabled: false,
  meta_tag_attributes: {},
  pin_to_top: false,
};

export const pickPostSettingsFormValues = (
  source: EditablePost | PostSettingsFormValues,
): PostSettingsFormValues => ({
  cardview_thumbnail: source.cardview_thumbnail,
  created_at: source.created_at,
  hide_from_featured_areas: Boolean(source.hide_from_featured_areas),
  hide_meta_info: Boolean(source.hide_meta_info),
  internal_custom_html: source.internal_custom_html,
  is_comments_closed: Boolean(source.is_comments_closed),
  is_comments_disabled: Boolean(source.is_comments_disabled),
  is_liking_disabled: Boolean(source.is_liking_disabled),
  is_truncation_disabled: Boolean(source.is_truncation_disabled),
  meta_tag_attributes: source.meta_tag_attributes ?? {},
  pin_to_top: Boolean(source.pin_to_top),
  pinned_at: source.pinned_at,
  slug: source.slug,
  user_id: source.user_id,
});

export const buildPostFormPayload = (
  formData: PostFormValues,
  isEdit: boolean,
  defaultValues: PostFormValues,
): QuickPostV3CreateOrUpdateParams => ({
  post: isEdit
    ? {
        id: defaultValues.id ?? undefined,
        slug: defaultValues.slug,
        space_id: defaultValues.space_id,
      }
    : { space_id: formData.space_id },
  formData: isEdit ? formData : omit(formData, "id"),
});

export const createOrUpdateQuickPostV3 = (
  params: QuickPostV3CreateOrUpdateParams,
) =>
  postApi.createOrUpdate({
    post: params.post,
    formData: params.formData as Partial<PostFormData>,
  });
